import React, { useState } from 'react';
import Utilities from '@util/Utilities';
import { HandThumbDownIcon, HandThumbUpIcon } from '@heroicons/react/24/outline';
import {
	findSentimentTextColorClass,
} from '@app/site/secured/review-manager/fragment/property-overview/component/SentimentColors';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { ArrowTrendingDownIcon, ArrowTrendingUpIcon } from '@heroicons/react/20/solid';
import ReviewsStateStatistics from '@app/model/review/state/ReviewsStateStatistics';
import Tag from '@app/component/tag/Tag';
import ReviewsCategoryAnalysisDrawer
	from '@app/site/secured/review-manager/fragment/property-overview/component/drawer/ReviewsCategoryAnalysisDrawer';
import ReviewCategoryOutput from '@app/model/review/output/ReviewCategoryOutput';
import CompetitorReviewsState from '@app/model/review/state/CompetitorReviewsState';
import StateStatisticsForCategory from '@app/model/review/state/StateStatisticsForCategory';


const ReviewsPropertyPerformanceTable: React.FunctionComponent<{
	selectedHotelReviewsState: CompetitorReviewsState,
	comparisonHotelReviewsState?: CompetitorReviewsState
}> = ({ selectedHotelReviewsState, comparisonHotelReviewsState }) => {

	const [selectedCategory, setSelectedCategory] = useState<{
		hotelId: number
		category: ReviewCategoryOutput,
		statistics: ReviewsStateStatistics
	}>();


	const onOpenAnalysis = (category: ReviewCategoryOutput, statistics: ReviewsStateStatistics) => {
		setSelectedCategory({ hotelId: selectedHotelReviewsState.hotelId, category, statistics });
	};
	const hasComparisonData = hasComparisonCategoryData(comparisonHotelReviewsState);

	return <>
		{selectedCategory &&
			<ReviewsCategoryAnalysisDrawer {...selectedCategory} onClose={() => setSelectedCategory(undefined)} />}
		<ReviewsGuestFocusDistribution categoryStates={selectedHotelReviewsState.stateStatisticsForRootCategories} />
		<div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
			<div className="col-span-2">
				<div className="px-3 mb-1">
					<h2 className="text-base font-semibold text-gray-900">Market Demand</h2>
				</div>
				<div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
					<div className="overflow-x-auto">
						<div className="inline-block min-w-full align-middle">
							<table className="min-w-full divide-y divide-gray-300">
								<thead className="border-b border-gray-300 bg-gray-50">
								<tr>
									<th scope="col" colSpan={2}
											className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Category
									</th>
									<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Positivity</th>
									<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Score</th>
									<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Sentiment</th>
									<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Impact</th>
									{hasComparisonData && <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Change</th>}
									<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
									<th></th>
								</tr>
								</thead>
								{selectedHotelReviewsState.stateStatisticsForRootCategories.map(stateStatisticsForRootCategory => {
									return <MarketDemandOverviewSectionRow
										key={stateStatisticsForRootCategory.category.id}
										reviewsParentCategoryStates={stateStatisticsForRootCategory}
										reviewsCategoryStates={selectedHotelReviewsState.stateStatisticsForCategories}
										compareReviewsHotelStatistics={comparisonHotelReviewsState}
										onOpenAnalysis={onOpenAnalysis} />;
								})}
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>
	</>;
};

const guestFocusColors = [
	{ bar: 'bg-sky-500', dot: 'bg-sky-500' },
	{ bar: 'bg-amber-500', dot: 'bg-amber-500' },
	{ bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
	{ bar: 'bg-indigo-500', dot: 'bg-indigo-500' },
	{ bar: 'bg-fuchsia-500', dot: 'bg-fuchsia-500' },
	{ bar: 'bg-violet-500', dot: 'bg-violet-500' },
];

const ReviewsGuestFocusDistribution: React.FunctionComponent<{
	categoryStates: StateStatisticsForCategory[]
}> = ({ categoryStates }) => {
	const categories = [...categoryStates]
		.filter(categoryState => categoryState.statistics.totalMentions > 0)
		.sort((a, b) => b.statistics.totalMentions - a.statistics.totalMentions)
		.slice(0, 6);
	const totalMentions = categories.reduce((sum, categoryState) => sum + categoryState.statistics.totalMentions, 0);

	if (totalMentions === 0) {
		return null;
	}

	return <section className="mb-8 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
		<div className="px-5 py-4">
			<h2 className="text-base font-semibold text-gray-900">Where Guests Focus</h2>
			<p className="mt-1 text-sm text-gray-500">{totalMentions.toLocaleString()} mentions</p>
		</div>
		<div className="border-t border-gray-200 px-5 py-4">
			<div className="flex h-7 overflow-hidden rounded-md">
				{categories.map((categoryState, index) => {
					const percentage = categoryState.statistics.totalMentions / totalMentions * 100;
					const color = guestFocusColors[index % guestFocusColors.length];

					return <div
						key={categoryState.category.id}
						className={`${color.bar} flex min-w-10 items-center justify-center text-xs font-bold text-white`}
						style={{ width: `${percentage}%` }}>
						{Utilities.formatDouble(percentage, 0)}%
					</div>;
				})}
			</div>
			<div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
				{categories.map((categoryState, index) => {
					const color = guestFocusColors[index % guestFocusColors.length];

					return <div key={categoryState.category.id} className="flex min-w-0 items-center gap-2 text-sm text-gray-700">
						<span className={`${color.dot} h-3 w-3 shrink-0 rounded-full`} />
						<span className="truncate">{categoryState.category.name}</span>
					</div>;
				})}
			</div>
		</div>
	</section>;
};

const MarketDemandOverviewSectionRow: React.FunctionComponent<{
	reviewsParentCategoryStates: StateStatisticsForCategory,
	reviewsCategoryStates: StateStatisticsForCategory[],
	compareReviewsHotelStatistics?: CompetitorReviewsState,
	onOpenAnalysis: (category: ReviewCategoryOutput, statistics: ReviewsStateStatistics) => void
}> = ({ reviewsParentCategoryStates, reviewsCategoryStates, compareReviewsHotelStatistics, onOpenAnalysis }) => {

	const [isOpen, setIsOpen] = useState(false);
	const hasComparisonData = hasComparisonCategoryData(compareReviewsHotelStatistics);
	const childCategoryStates = reviewsParentCategoryStates.category.children
		.map(childCategory => reviewsCategoryStates.find(s => s.category.id === childCategory.id))
		.filter((state): state is StateStatisticsForCategory => !!state);
	const compareCategoryStatistics = findComparisonCategoryStatistics(compareReviewsHotelStatistics, reviewsParentCategoryStates.category.id);

	return <tbody className={Utilities.classNames('divide-y divide-gray-200', !isOpen ? 'bg-white' : 'bg-gray-50')}>
	<MarketDemandOverviewRow
		category={reviewsParentCategoryStates.category}
		statistics={reviewsParentCategoryStates.statistics}
		hasComparisonData={hasComparisonData}
		compareCategoryStatistics={compareCategoryStatistics}
		isOpen={isOpen}
		setIsOpen={setIsOpen}
		onOpenAnalysis={() => onOpenAnalysis(reviewsParentCategoryStates.category, reviewsParentCategoryStates.statistics)}
	/>
	{isOpen && childCategoryStates.map((categoryStatistics) => {
		return <MarketDemandOverviewRow key={categoryStatistics.category.id}
										category={categoryStatistics.category}
										statistics={categoryStatistics.statistics}
										hasComparisonData={hasComparisonData}
										compareCategoryStatistics={findComparisonCategoryStatistics(compareReviewsHotelStatistics, categoryStatistics.category.id)}
										onOpenAnalysis={() => onOpenAnalysis(categoryStatistics.category, categoryStatistics.statistics)}
										isSubCategory />;
	})}
	</tbody>;
};


const MarketDemandOverviewRow: React.FunctionComponent<{
	category: ReviewCategoryOutput,
	statistics: ReviewsStateStatistics
	hasComparisonData: boolean
	compareCategoryStatistics?: StateStatisticsForCategory
	isOpen?: boolean
	setIsOpen?: (isOpen: boolean) => void
	isSubCategory?: boolean,
	onOpenAnalysis: () => void
}> = ({ category, statistics, hasComparisonData, compareCategoryStatistics, isSubCategory = false, isOpen, setIsOpen, onOpenAnalysis }) => {

	const renderTag = () => {
		if (statistics.totalImpact == 0) return '';

		if (statistics.totalImpact > 0) {
			return <Tag state="green">Optimal</Tag>;
		} else if (statistics.totalImpact < 0) {
			return <Tag state="yellow">Warning</Tag>;
		} else {
			return <Tag state="blue">Stable</Tag>;
		}
	};

	const renderScoreChange = () => {
		if (!compareCategoryStatistics) return '-';

		const scoreChange = statistics.score - compareCategoryStatistics.statistics.score;

		if (scoreChange > 0) return <span className="inline-flex items-center gap-x-1 text-xs font-semibold text-green-600">
			<ArrowTrendingUpIcon className="h-4 w-4" />
			+{Utilities.formatDouble(scoreChange, 1)}
		</span>;

		if (scoreChange < 0) return <span className="inline-flex items-center gap-x-1 text-xs font-semibold text-red-600">
			<ArrowTrendingDownIcon className="h-4 w-4" />
			{Utilities.formatDouble(scoreChange, 1)}
		</span>;

		return <span className="text-xs font-medium text-gray-500">0.0</span>;
	};

	const categoryName = category.name;
	const toggleOpen = () => setIsOpen?.(!(isOpen === true));

	if (categoryName.length === 0) return null;

	return <>
		<tr>
			{!isSubCategory && <>
				<td className="whitespace-nowrap pl-3 py-4 text-sm text-gray-600 w-5">
					<a
						onClick={toggleOpen}
						className="inline-flex items-center rounded-md p-1 text-gray-600 hover:text-blue-800 hover:bg-gray-100 ring-0 focus:ring-2 focus:ring-blue-500 cursor-pointer"
					>
						{isOpen === true ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
					</a>
				</td>
				<td className="whitespace-nowrap px-3 py-4 text-sm font-bold text-gray-900">
					<span className="cursor-pointer" onClick={toggleOpen}>{categoryName}</span>
					<span
						className="block text-xs font-normal text-gray-500">{statistics.totalMentions} mentions</span>
				</td>
			</>}

			{isSubCategory && <>
				<td />
				<td className="whitespace-nowrap px-3 py-2 text-xs font-bold text-gray-900 sm:pl-8">
					<span>{categoryName}</span><span
					className="block text-xs font-normal text-gray-500">{statistics.totalMentions} mentions</span>
				</td>
			</>}

			<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
				<div className="flex items-center gap-x-2">
					<div className="w-20 h-1.5 relative">
						<div className="w-full overflow-hidden absolute h-1.5 flex rounded bg-gray-200">
							<div className="h-full bg-blue-500" style={{ width: `${statistics.positivityRate * 100}%` }}></div>
						</div>
					</div>
					<span
						className="inline text-xs font-bold text-gray-800">{Utilities.formatDouble(statistics.positivityRate * 100, 0)}%</span>
				</div>
			</td>
			<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
				<div className="flex items-center gap-x-2">
				<span
					className={`font-bold text-base ${findSentimentTextColorClass(statistics.score * 100)}`}>
					{Utilities.formatDouble(statistics.score, 1, '0.0', true)}
				</span>
				</div>
			</td>
			<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
				<div className="flex items-center gap-x-4">
					<div className="flex items-center gap-x-1">
						<HandThumbUpIcon className="w-4 h-4 text-green-500" />
						<span
							className="text-xs font-normal text-gray-800">{statistics.positiveSentimentCount}</span>
					</div>
					<div className="flex items-center gap-x-1">
						<HandThumbDownIcon className="w-4 h-4 text-red-500" />
						<span
							className="text-xs font-normal text-gray-800">{statistics.negativeSentimentCount}</span>
					</div>
				</div>
			</td>
			<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600"><span
				className={`inline-flex items-center rounded-md  px-1.5 py-0.5 text-xs font-medium  ring-1 ring-inset ${statistics.totalImpact >= 0 ? 'bg-green-50 text-green-700 ring-green-700/10' : 'bg-red-50 text-red-700 ring-red-700/10'}`}>
				{statistics.totalImpact > 0 ? '+' : ''}{Utilities.formatDouble(statistics.totalImpact, 2)}
			</span>
			</td>
			{hasComparisonData && <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
				{renderScoreChange()}
			</td>}
			<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
				{renderTag()}
			</td>
			<td>
				<a className="font-semibold text-sm text-blue-600 hover:text-blue-500 cursor-pointer" onClick={onOpenAnalysis}>Details</a>
			</td>
		</tr>
	</>;
};

const findComparisonCategoryStatistics = (compareReviewsHotelStatistics: CompetitorReviewsState | undefined, categoryId: number) => {
	return compareReviewsHotelStatistics?.stateStatisticsForCategories.find(s => s.category.id === categoryId);
};

const hasComparisonCategoryData = (compareReviewsHotelStatistics: CompetitorReviewsState | undefined) => {
	return (compareReviewsHotelStatistics?.stateStatisticsForCategories.length ?? 0) > 0;
};

export default ReviewsPropertyPerformanceTable;
